<script lang="ts" setup>
import {
  PhBuildings,
  PhCalendarBlank,
  PhCalendarCheck,
  PhCalendarDots,
  PhCalendarStar,
  PhCalendarX,
  PhCircleNotch,
  PhCurrencyCircleDollar,
  PhFlag,
  PhFloppyDisk,
  PhHandshake,
  PhInfo,
  PhPencilSimple,
  PhPercent,
  PhRuler,
  PhScales,
  PhTag,
  PhX,
} from '@phosphor-icons/vue';
import { formatNumber, formatNumberWithDecimal } from 'cesg-shared';
import {
  ContractorType,
  ProjectType,
} from 'cesg-shared/dist/models/schemas/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useProjectMetaDataLogic } from './logic/useProjectMetaDataLogic';
import { BlurField } from './sub-components/blur-field';
import type { ProjectMetaData, UseProjectMetaDataLogicProps } from './types';

const { t } = useI18n();

const props = defineProps<UseProjectMetaDataLogicProps>();

const emit = defineEmits<{
  save: [data: ProjectMetaData];
}>();

const { state, action } = useProjectMetaDataLogic(props, emit);

const statusModel = computed({
  get: () => state.metaData.status,
  set: (value: string) => action.updateField({ field: 'status', value }),
});

const typeModel = computed({
  get: () => state.metaData.type,
  set: (value: string) => action.updateField({ field: 'type', value }),
});

const contractorTypeModel = computed({
  get: () => state.metaData.contractorType,
  set: (value: string) =>
    action.updateField({ field: 'contractorType', value }),
});

const sizeModel = computed({
  get: () => formatNumber(state.metaData.size),
  set: (value: string) => action.updateField({ field: 'size', value }),
});

const compensationFormModel = computed({
  get: () => state.metaData.compensationForm,
  set: (value: string) =>
    action.updateField({ field: 'compensationForm', value }),
});

const arvodeModel = computed({
  get: () =>
    state.metaData.arvode != null
      ? String(state.metaData.arvode).replace('.', ',')
      : '',
  set: (value: string) => action.updateField({ field: 'arvode', value }),
});

const startDateModel = computed({
  get: () => state.metaData.startDate,
  set: (value: string) => action.updateField({ field: 'startDate', value }),
});

const samprovDateModel = computed({
  get: () => state.metaData.samprovDate,
  set: (value: string) => action.updateField({ field: 'samprovDate', value }),
});

const douinDateModel = computed({
  get: () => state.metaData.douinDate,
  set: (value: string) => action.updateField({ field: 'douinDate', value }),
});

const endDateModel = computed({
  get: () => state.metaData.endDate,
  set: (value: string) => action.updateField({ field: 'endDate', value }),
});

const handoverDateModel = computed({
  get: () => state.metaData.handoverDate,
  set: (value: string) => action.updateField({ field: 'handoverDate', value }),
});

const fdobDateModel = computed({
  get: () => state.metaData.fdobDate,
  set: (value: string) => action.updateField({ field: 'fdobDate', value }),
});

const ataPercentage = computed(() =>
  formatNumberWithDecimal(state.percentageAta, 1),
);
const contractorSum = computed(() => formatNumber(state.contractorSum));
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">
        <PhInfo :size="18" class="mr-2" />
        #{{ state.metaData.number }}&nbsp;{{ state.metaData.name }}
      </p>
      <button
        v-if="state.mode === 'edit'"
        class="header-btn cancel-btn"
        @click="action.cancel()"
      >
        <PhX :size="15" />
        {{ t('overview.projectMetaData.cancelButton') }}
      </button>
      <button
        v-if="state.mode !== 'read-only'"
        class="header-btn edit-btn"
        :class="{ 'is-active': state.mode === 'edit' }"
        :disabled="
          state.mode === 'saving' ||
          (state.mode === 'edit' && !state.hasChanges)
        "
        @click="action.toggleEdit()"
      >
        <PhCircleNotch v-if="state.mode === 'saving'" :size="15" class="spin" />
        <PhFloppyDisk v-else-if="state.mode === 'edit'" :size="15" />
        <PhPencilSimple v-else :size="15" />
        {{
          state.mode === 'edit'
            ? t('overview.projectMetaData.saveButton')
            : t('overview.projectMetaData.editButton')
        }}
      </button>
    </div>

    <div class="card-content meta-content">
      <!-- Status group -->
      <div class="status-group">
        <div class="status-item">
          <span class="status-icon"><PhTag :size="16" /></span>
          <div class="status-text">
            <span class="status-label">{{
              t('overview.projectMetaData.statusLabel')
            }}</span>
            <template v-if="state.mode === 'edit'">
              <select class="status-select" v-model="statusModel">
                <option value="">
                  {{ t('overview.projectMetaData.choosePlaceholder') }}
                </option>
                <option value="Aktivt">
                  {{ t('overview.projectMetaData.statusActive') }}
                </option>
                <option value="Avslutat">
                  {{ t('overview.projectMetaData.statusDone') }}
                </option>
              </select>
            </template>
            <template v-else>
              <span class="status-value">{{
                state.metaData.status || '–'
              }}</span>
            </template>
          </div>
        </div>
        <div class="status-item">
          <span class="status-icon"><PhBuildings :size="16" /></span>
          <div class="status-text">
            <span class="status-label">{{
              t('overview.projectMetaData.projectTypeLabel')
            }}</span>
            <template v-if="state.mode === 'edit'">
              <select class="status-select" v-model="typeModel">
                <option value="">
                  {{ t('overview.projectMetaData.choosePlaceholder') }}
                </option>
                <option :value="ProjectType.HGA">
                  {{ t('overview.projectMetaData.typeHGA') }}
                </option>
                <option :value="ProjectType.ROT">
                  {{ t('overview.projectMetaData.typeROT') }}
                </option>
                <option :value="ProjectType.SERVICE">
                  {{ t('overview.projectMetaData.typeService') }}
                </option>
              </select>
            </template>
            <template v-else>
              <span class="status-value">{{ state.metaData.type || '–' }}</span>
            </template>
          </div>
        </div>
        <div class="status-item">
          <span class="status-icon"><PhHandshake :size="16" /></span>
          <div class="status-text">
            <span class="status-label">{{
              t('overview.projectMetaData.contractorTypeLabel')
            }}</span>
            <template v-if="state.mode === 'edit'">
              <select class="status-select" v-model="contractorTypeModel">
                <option value="">
                  {{ t('overview.projectMetaData.choosePlaceholder') }}
                </option>
                <option :value="ContractorType.TOTAL">
                  {{ t('overview.projectMetaData.contractorTypeTotal') }}
                </option>
                <option :value="ContractorType.EXECUTION">
                  {{ t('overview.projectMetaData.contractorTypeExecution') }}
                </option>
              </select>
            </template>
            <template v-else>
              <span class="status-value">{{
                state.metaData.contractorType === ContractorType.TOTAL
                  ? t('overview.projectMetaData.contractorTypeTotal')
                  : state.metaData.contractorType === ContractorType.EXECUTION
                    ? t('overview.projectMetaData.contractorTypeExecution')
                    : state.metaData.contractorType || '–'
              }}</span>
            </template>
          </div>
        </div>
        <div class="status-item">
          <span class="status-icon"><PhRuler :size="16" /></span>
          <div class="status-text">
            <span class="status-label">{{
              t('overview.projectMetaData.sizeLabel')
            }}</span>
            <span class="status-value">
              <template v-if="state.mode === 'edit'">
                <input
                  class="inline-input size-input"
                  type="text"
                  inputmode="numeric"
                  v-model.lazy="sizeModel"
                  :size="sizeModel.length || 4"
                />
                {{ t('overview.projectMetaData.sizeUnit') }}
              </template>
              <template v-else
                >{{ formatNumber(state.metaData.size) }}
                {{ t('overview.projectMetaData.sizeUnit') }}</template
              >
            </span>
          </div>
        </div>
      </div>

      <hr class="my-4" />

      <div class="columns is-align-items-stretch">
        <!-- Left: Financials -->
        <div class="column is-one-third">
          <div class="finance-group">
            <p class="finance-group-header">
              {{ t('overview.projectMetaData.compensationHeader') }}
            </p>
            <div class="meta-field">
              <span class="meta-icon"><PhHandshake :size="18" /></span>
              <div>
                <p class="heading">
                  {{ t('overview.projectMetaData.compensationFormLabel') }}
                </p>
                <p class="meta-value">
                  <template v-if="state.mode === 'edit'">
                    <select
                      class="status-select"
                      v-model="compensationFormModel"
                    >
                      <option value="">
                        {{ t('overview.projectMetaData.choosePlaceholder') }}
                      </option>
                      <option value="Fast">
                        {{ t('overview.projectMetaData.compensationFixed') }}
                      </option>
                      <option value="Löpande">
                        {{ t('overview.projectMetaData.compensationOngoing') }}
                      </option>
                      <option value="Incitament">
                        {{
                          t('overview.projectMetaData.compensationIncentive')
                        }}
                      </option>
                      <option value="Takpris">
                        {{ t('overview.projectMetaData.compensationMaxPrice') }}
                      </option>
                      <option value="Annat">
                        {{ t('overview.projectMetaData.compensationOther') }}
                      </option>
                    </select>
                  </template>
                  <template v-else>{{
                    state.metaData.compensationForm || '–'
                  }}</template>
                </p>
              </div>
            </div>
            <div class="meta-field" v-if="state.arvodeVisible">
              <span class="meta-icon"><PhPercent :size="18" /></span>
              <div>
                <p class="heading">
                  {{ t('overview.projectMetaData.arvodeLabel') }}
                </p>
                <p class="meta-value">
                  <template v-if="state.mode === 'edit'">
                    <input
                      class="inline-input pct-input"
                      type="text"
                      inputmode="decimal"
                      v-model.lazy="arvodeModel"
                    />
                    %
                  </template>
                  <template v-else>{{
                    state.metaData.arvode != null
                      ? formatNumberWithDecimal(state.metaData.arvode, 1) + ' %'
                      : '–'
                  }}</template>
                </p>
              </div>
            </div>
            <div class="meta-field">
              <span class="meta-icon"
                ><PhCurrencyCircleDollar :size="18"
              /></span>
              <div>
                <p class="heading">
                  {{ t('overview.projectMetaData.contractSumLabel') }}
                </p>
                <p class="meta-value">
                  {{ formatNumber(state.metaData.contractSum) }}
                  {{ t('overview.projectMetaData.currencyUnit') }}
                </p>
              </div>
            </div>
            <div class="meta-field">
              <span class="meta-icon"><PhScales :size="18" /></span>
              <div>
                <p class="heading">
                  {{ t('overview.projectMetaData.ataSumLabel') }}
                </p>
                <p class="meta-value">
                  {{ formatNumber(state.metaData.ataSum) }}
                  {{ t('overview.projectMetaData.currencyUnit') }}
                  <span class="earnings-pct"> ({{ ataPercentage }} %) </span>
                </p>
              </div>
            </div>
            <div class="meta-field" v-if="!state.resultHidden">
              <span class="meta-icon"><PhScales :size="18" /></span>
              <BlurField :should-blur="state.resultBlurred">
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.earningsLabel') }}
                  </p>
                  <p class="meta-value">
                    {{ formatNumber(state.metaData.earnings) }}
                    {{ t('overview.projectMetaData.currencyUnit') }}
                    <span class="earnings-pct">
                      ({{
                        formatNumberWithDecimal(
                          state.metaData.earningsPercent,
                          1,
                        )
                      }}
                      %)
                    </span>
                  </p>
                </div>
              </BlurField>
            </div>
            <div class="meta-field contractor-sum">
              <span class="meta-icon"
                ><PhCurrencyCircleDollar :size="18"
              /></span>
              <div>
                <p class="heading">
                  {{ t('overview.projectMetaData.contractorSumLabel') }}
                </p>
                <p class="meta-value">
                  {{ contractorSum }}
                  {{ t('overview.projectMetaData.currencyUnit') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Dates -->
        <div class="column dates-column">
          <div class="date-group date-group-stretch">
            <p class="date-group-header">
              {{ t('overview.projectMetaData.timelineHeader') }}
            </p>
            <div class="date-row">
              <div class="meta-field">
                <span class="meta-icon date-meta-icon"
                  ><PhCalendarBlank :size="18"
                /></span>
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.startDateLabel') }}
                  </p>
                  <p class="meta-value">
                    <template v-if="state.mode === 'edit'">
                      <span class="date-edit-field">
                        <input
                          class="inline-input date-input"
                          type="text"
                          inputmode="numeric"
                          :placeholder="
                            t('overview.projectMetaData.datePlaceholder')
                          "
                          v-model.lazy="startDateModel"
                          :size="startDateModel.length || 10"
                        />
                        <span class="date-picker-icon">
                          <PhCalendarBlank :size="14" />
                          <input
                            type="date"
                            class="date-picker-overlay"
                            v-model="startDateModel"
                          />
                        </span>
                      </span>
                    </template>
                    <template v-else>{{
                      state.metaData.startDate || '–'
                    }}</template>
                  </p>
                </div>
              </div>
              <div class="meta-field">
                <span class="meta-icon date-meta-icon"
                  ><PhCalendarDots :size="18"
                /></span>
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.samprovLabel') }}
                  </p>
                  <p class="meta-value">
                    <template v-if="state.mode === 'edit'">
                      <span class="date-edit-field">
                        <input
                          class="inline-input date-input"
                          type="text"
                          inputmode="numeric"
                          :placeholder="
                            t('overview.projectMetaData.datePlaceholder')
                          "
                          v-model.lazy="samprovDateModel"
                          :size="samprovDateModel.length || 10"
                        />
                        <span class="date-picker-icon">
                          <PhCalendarBlank :size="14" />
                          <input
                            type="date"
                            class="date-picker-overlay"
                            v-model="samprovDateModel"
                          />
                        </span>
                      </span>
                    </template>
                    <template v-else>{{
                      state.metaData.samprovDate || '–'
                    }}</template>
                  </p>
                </div>
              </div>
              <div class="meta-field">
                <span class="meta-icon date-meta-icon"
                  ><PhCalendarStar :size="18"
                /></span>
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.douinDateLabel') }}
                  </p>
                  <p class="meta-value">
                    <template v-if="state.mode === 'edit'">
                      <span class="date-edit-field">
                        <input
                          class="inline-input date-input"
                          type="text"
                          inputmode="numeric"
                          :placeholder="
                            t('overview.projectMetaData.datePlaceholder')
                          "
                          v-model.lazy="douinDateModel"
                          :size="douinDateModel.length || 10"
                        />
                        <span class="date-picker-icon">
                          <PhCalendarBlank :size="14" />
                          <input
                            type="date"
                            class="date-picker-overlay"
                            v-model="douinDateModel"
                          />
                        </span>
                      </span>
                    </template>
                    <template v-else>{{
                      state.metaData.douinDate || '–'
                    }}</template>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="date-group date-group-stretch">
            <p class="date-group-header">
              {{ t('overview.projectMetaData.closeOutHeader') }}
            </p>
            <div class="date-row">
              <div class="meta-field">
                <span class="meta-icon date-meta-icon"
                  ><PhCalendarX :size="18"
                /></span>
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.endDateLabel') }}
                  </p>
                  <p class="meta-value">
                    <template v-if="state.mode === 'edit'">
                      <span class="date-edit-field">
                        <input
                          class="inline-input date-input"
                          type="text"
                          inputmode="numeric"
                          :placeholder="
                            t('overview.projectMetaData.datePlaceholder')
                          "
                          v-model.lazy="endDateModel"
                          :size="endDateModel.length || 10"
                        />
                        <span class="date-picker-icon">
                          <PhCalendarBlank :size="14" />
                          <input
                            type="date"
                            class="date-picker-overlay"
                            v-model="endDateModel"
                          />
                        </span>
                      </span>
                    </template>
                    <template v-else>{{
                      state.metaData.endDate || '–'
                    }}</template>
                  </p>
                </div>
              </div>
              <div class="meta-field">
                <span class="meta-icon date-meta-icon"
                  ><PhCalendarCheck :size="18"
                /></span>
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.handoverDateLabel') }}
                  </p>
                  <p class="meta-value">
                    <template v-if="state.mode === 'edit'">
                      <span class="date-edit-field">
                        <input
                          class="inline-input date-input"
                          type="text"
                          inputmode="numeric"
                          :placeholder="
                            t('overview.projectMetaData.datePlaceholder')
                          "
                          v-model.lazy="handoverDateModel"
                          :size="handoverDateModel.length || 10"
                        />
                        <span class="date-picker-icon">
                          <PhCalendarBlank :size="14" />
                          <input
                            type="date"
                            class="date-picker-overlay"
                            v-model="handoverDateModel"
                          />
                        </span>
                      </span>
                    </template>
                    <template v-else>{{
                      state.metaData.handoverDate || '–'
                    }}</template>
                  </p>
                </div>
              </div>
              <div class="meta-field">
                <span class="meta-icon date-meta-icon"
                  ><PhFlag :size="18"
                /></span>
                <div>
                  <p class="heading">
                    {{ t('overview.projectMetaData.fdobDateLabel') }}
                  </p>
                  <p class="meta-value">
                    <template v-if="state.mode === 'edit'">
                      <span class="date-edit-field">
                        <input
                          class="inline-input date-input"
                          type="text"
                          inputmode="numeric"
                          :placeholder="
                            t('overview.projectMetaData.datePlaceholder')
                          "
                          v-model.lazy="fdobDateModel"
                          :size="fdobDateModel.length || 10"
                        />
                        <span class="date-picker-icon">
                          <PhCalendarBlank :size="14" />
                          <input
                            type="date"
                            class="date-picker-overlay"
                            v-model="fdobDateModel"
                          />
                        </span>
                      </span>
                    </template>
                    <template v-else>{{
                      state.metaData.fdobDate || '–'
                    }}</template>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 1;
}

.header-btn {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.edit-btn.is-active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.edit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.cancel-btn {
  margin-right: 0.4rem;
}

.cancel-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  margin: 0;
  height: 1.5em;
  vertical-align: middle;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: 1.5;
  color: inherit;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.inline-input::placeholder {
  color: #bbb;
}

.header-name-input {
  flex: 1;
  min-width: 0;
}

.status-input {
  width: 100%;
  min-width: 3rem;
}

.status-select {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0 1rem 0 0;
  margin: 0;
  height: 1.5em;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.5;
  color: #222;
  cursor: pointer;
  appearance: auto;
  transition: box-shadow 0.15s;
}

.status-select:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.status-select:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.amount-input {
  text-align: left;
  width: 7rem;
}

.input-unit-wrap {
  display: flex;
  align-items: center;
  gap: 0.25em;
}

.size-input {
  text-align: left;
  width: 100%;
}

.pct-input {
  width: 4rem;
  text-align: right;
}

.date-edit-field {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.date-picker-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  color: #555;
  cursor: pointer;
}

.date-picker-icon:hover {
  color: var(--primary-color);
}

.date-picker-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  padding: 0;
  margin: 0;
  border: none;
}

.meta-content {
  background-color: #fafafc;
}

.meta-content hr {
  background-color: #c4c4cc;
}

.meta-field {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.meta-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background-color: #ededf0;
  color: #555;
}

:deep(.heading),
.heading {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #777;
  margin-bottom: 0.2rem;
}

.meta-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  display: flex;
  align-items: center;
  gap: 0.25em;
  flex-wrap: wrap;
}

.status-group {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.status-item {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
}

.status-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background-color: #ededf0;
  color: #555;
}

.status-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  flex: 1;
}

.status-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #777;
}

.status-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
}

.finance-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  height: 100%;
  justify-content: space-between;
}

.finance-group-header {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #777;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.5rem;
  margin-bottom: 0.25rem;
}

.date-group-stretch > .date-row {
  margin-top: auto;
  margin-bottom: auto;
}

.date-row {
  display: flex;
  gap: 1rem;
}

.date-row > .meta-field {
  flex: 1;
  min-width: 0;
}

.dates-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.date-group-stretch {
  flex: 1;
}

.date-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
}

.date-group-header {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #777;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.5rem;
  margin-bottom: 0.25rem;
}

.earnings-pct {
  font-weight: 400;
  color: #666;
}

.contractor-sum {
  padding-top: 0.75rem;
  border-top: 1px solid #e0e0e0;
}

.spin {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .date-meta-icon {
    display: none;
  }
}
</style>
